import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/auth";

const KINDS = ["all", "volunteer", "buddy", "staff"];
const STATUSES = ["all", "active", "inactive", "suspended"];
const DOMAINS = [
  "legal", "housing", "immigration", "retail", "nourishment",
  "wellness", "trainers", "coaches", "therapists", "aesthetics",
];

export default function People() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [domain, setDomain] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (kind !== "all") params.kind = kind;
    if (status !== "all") params.status = status;
    if (domain) params.domain = domain;
    if (q.trim()) params.q = q.trim();
    api.get("/ops/people", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [kind, status, domain, q]);

  return (
    <div className="space-y-5" data-testid="ops-people-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">People</h1>
        <div className="flex flex-wrap gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email" className="text-sm border rounded px-3 py-1 bg-white" data-testid="ops-people-search" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="ops-people-kind-filter" className="text-sm border rounded px-2 py-1 bg-white">
            {KINDS.map((k) => <option key={k} value={k}>Type: {k}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ops-people-status-filter" className="text-sm border rounded px-2 py-1 bg-white">
            {STATUSES.map((s) => <option key={s} value={s}>Status: {s}</option>)}
          </select>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="ops-people-domain-filter" className="text-sm border rounded px-2 py-1 bg-white">
            <option value="">Domain: any</option>
            {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      {err && <div className="text-sm text-red-700">{err}</div>}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-4 py-2">Name</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Domains</th>
            <th className="text-left px-4 py-2">Last activity</th>
            <th className="text-right px-4 py-2">Open</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No people match the current filter.</td></tr>}
            {items.map((p) => (
              <tr key={`${p.kind}:${p.id}`} className="border-t hover:bg-slate-50" data-testid="ops-people-row">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{p.name || "—"}</div>
                  <div className="text-xs text-slate-500">{p.email}</div>
                </td>
                <td className="px-4 py-3 capitalize">{p.kind}{p.operator_role ? ` · ${p.operator_role}` : ""}</td>
                <td className="px-4 py-3"><StatusBadge s={p.status} /></td>
                <td className="px-4 py-3">{(p.domain_keys || []).length === 0 ? <span className="text-slate-400">—</span> : (p.domain_keys || []).slice(0, 4).map((d) => <span key={d} className="mr-1 px-1.5 py-0.5 text-xs rounded bg-slate-100">{d}</span>)}</td>
                <td className="px-4 py-3 text-slate-500">{(p.last_activity || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/ops/people/${p.kind}/${p.id}`} className="text-slate-900 font-medium" data-testid={`ops-people-open-${p.id}`}>Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = { active: "bg-emerald-100 text-emerald-800", inactive: "bg-slate-100 text-slate-700", suspended: "bg-amber-100 text-amber-800" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
}
